<script setup lang="ts">
import {
  Case,
  CaseListService,
  CaseReevaluationService,
  CaseServiceInterface,
  ContainerTypes,
  Contexts,
  NamedBindings,
  TranslationService,
  getContainer,
  CaseListElement,
} from '@dandelion/core';
import { onMounted, PropType, ref } from 'vue';
import { Preferences } from '../../enum/Preferences';
import ConsentChoice from '../ConsentChoice.vue';
import { PreferencesService } from '../../services/PreferencesService';
import { NamedServices } from '../../enum/NamedServices';

const props = defineProps({
  caseListElement: {
    type: Object as PropType<CaseListElement>,
    required: true,
    default: {},
  },
});

const container = getContainer(Contexts.DEFAULT, ContainerTypes.REEVALUATION);
const caseService = container.get(
  NamedBindings.CaseService
) as CaseServiceInterface;
const caseListService = container.get(
  NamedBindings.CaseListService
) as CaseListService;
const reevaluationService = container.get(
  NamedBindings.CaseReevaluationService
) as CaseReevaluationService;
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;
const centrePreferencesService = container.get(
  NamedServices.CentrePreferencesService
) as PreferencesService;
const askConsentValue = (
  await centrePreferencesService.getPreference(Preferences.ASKCONSENT)
).value;

const selectedConsent = ref('');
const originalConsent = ref('');
const consentExemptMotivation = ref('');
const isLoading = ref(false);
const dialogRef = ref<HTMLDialogElement | null>(null);

onMounted(() => {
  document
    .getElementById('open-consent-choice-dialog-btn')
    ?.addEventListener('click', async function () {
      const { data } = await caseService.getDataFor(
        props.caseListElement?.caseMetaData?.caseID
      );
      originalConsent.value = data.consent;
      selectedConsent.value = originalConsent.value
        ? originalConsent.value
        : 'exempt';
      consentExemptMotivation.value = data.consentExemptMotivation;
    });
});

const setConsent = (consent: string, newConsentExemptMotivation: string) => {
  selectedConsent.value = consent;
  consentExemptMotivation.value = newConsentExemptMotivation;
};

async function saveConsent() {
  isLoading.value = true;
  const { data } = await caseService.getDataFor(
    props.caseListElement.caseMetaData.caseID
  );
  const caseMetaData = await caseService.getCaseMetaDataFor(
    props.caseListElement.caseMetaData.caseID
  );
  data.consent = selectedConsent.value;
  if(selectedConsent.value == 'exempt') {
    data.consentExemptMotivation = consentExemptMotivation.value;
  }
  if (selectedConsent.value == 'no') {
    data.icuAdmDate = data.core?.administrativeInfo?.icuAdmDate;
  }
  const caseForUpdate = await reevaluationService.reevaluateCase(
    new Case(props.caseListElement.caseMetaData.caseID, data, caseMetaData)
  );
  await caseService.update(caseForUpdate);
  await caseListService.updateCase(
    caseForUpdate.caseID,
    caseForUpdate.saveMetaData.lastUpdate
  );
  await caseListService.updateCaseList();
  const dialog = document.getElementById(
    'consentChoiceDialog'
  )! as HTMLDialogElement;
  dialog.close();
  isLoading.value = false;
}
</script>

<template>
  <button
    id="open-consent-choice-dialog-btn"
    style="display: none"
    @click="dialogRef!.showModal()"
  ></button>
  <dialog id="consentChoiceDialog" ref="dialogRef" class="modal">
    <div
      class="modal-content"
      v-if="askConsentValue !== 'false' && originalConsent !== 'no'"
    >
      <h2 id="consent-choice-title" v-html="ts.t('Manage Consent')"></h2>
      <ion-icon
        id="close-consent-choice-dialog-btn"
        name="close-outline"
        @click="dialogRef!.close()"
      />
      <ConsentChoice
        :consent="selectedConsent"
        :exemptMotivation="consentExemptMotivation"
        @setConsent="setConsent"
      ></ConsentChoice>
      <button @click="saveConsent()" id="confirm-consent-choice-btn">
        <span v-if="isLoading" class="button-spinner" />
        <span v-else v-html="ts.t('Confirm')" />
      </button>
    </div>
    <div
      class="message-wrapper"
      v-if="askConsentValue == 'false' && originalConsent !== 'no'"
    >
      <span
        class="ask-for-consent-false-msg"
        v-html="ts.t('askForConsent')"
      ></span>
      <ion-icon
        id="close-consent-choice-dialog-btn"
        name="close-outline"
        @click="dialogRef!.close()"
      />
    </div>
    <div
      class="message-wrapper"
      v-if="askConsentValue !== 'false' && originalConsent == 'no'"
    >
      <span
        class="ask-for-consent-false-msg"
        v-html="ts.t('Patient has denied consent, you cannot change it')"
      ></span>
      <ion-icon
        id="close-consent-choice-dialog-btn"
        name="close-outline"
        @click="dialogRef!.close()"
      />
    </div>
  </dialog>
</template>

<style>
#consentChoiceDialog {
  box-shadow: none;
}

#consentChoiceDialog .modal-content {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 2rem 2rem 6rem 2rem;
  border-radius: 0.5rem;
  position: relative;
}

#consentChoiceDialog #consent-choice-title {
  font-weight: 600;
  font-size: 2rem;
  margin: 1.5rem 0;
}

#consentChoiceDialog #consent-choice-label {
  display: none;
}

#consentChoiceDialog #consent-choice {
  width: 100%;
  margin: 1rem 0;
  height: 3rem;
  font-size: 1rem;
  font-weight: 600;
  background-color: var(--secondary-color);
  color: white;
  padding: 0 0.5rem;
  border: 0;
  outline: 0;
}

#consentChoiceDialog #close-consent-choice-dialog-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  color: red;
  border-radius: 0.5rem;
  cursor: pointer;
}

#consentChoiceDialog #close-consent-choice-dialog-btn:hover {
  color: white;
  background-color: red;
}

#consentChoiceDialog #confirm-consent-choice-btn {
  position: absolute;
  bottom: 1rem;
  right: 2rem;
}

#consentChoiceDialog .message-wrapper {
  position: relative;
}

#consentChoiceDialog .ask-for-consent-false-msg {
  display: flex;
  align-items: center;
  height: 5rem;
  background-color: red;
  border-radius: 0.5rem;
  color: white;
  font-weight: 600;
  font-size: 1.25rem;
  padding: 0 3rem;
}

#consentChoiceDialog .message-wrapper {
  position: relative;
}

#consentChoiceDialog .message-wrapper #close-consent-choice-dialog-btn {
  color: white;
  top: 0.75rem;
  right: 0.75rem;
  font-size: 1.5rem;
}

#consentChoiceDialog textarea {
  display: block;
  width: 100%;
  height: 5rem;
  background-color: var(--bg-color);
  color: #213547;
  border-bottom: 0.125rem solid var(--main-text-color);
  outline: none;
  border-radius: var(--rounded-btn, .5rem);
  padding: 0.25rem;
}

</style>
